/**
 * Task Field Primitives — SHARED between TaskCreateForm and TaskDetail.
 *
 * Both surfaces (create + edit) MUST use these primitives so the visual
 * structure stays in lockstep. Don't add a one-off field anywhere else.
 *
 * Public exports:
 *   - inputClasses / selectClasses — base classnames for inputs and selects
 *   - <FieldRow>      — `<h4>label</h4><children>` container
 *   - <FieldSelect>   — styled native <select> with chevron
 *   - <TaskMetadataGrid> — 2-col responsive grid that hosts metadata fields
 */

import { ChevronDown } from 'lucide-react';
import { forwardRef } from 'react';
import clsx from 'clsx';
import type { ReactNode, SelectHTMLAttributes } from 'react';

/** Base input/textarea classnames. Slightly stronger contrast than panel bg. */
export const inputClasses = clsx(
  'w-full rounded-lg py-[11px] pl-[13px] pr-3 text-sm font-medium transition-all',
  'bg-surface-base-light dark:bg-surface-raised-dark',
  'border border-border-subtle-light dark:border-border-subtle-dark',
  'text-text-primary-light dark:text-text-primary-dark',
  'placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark',
  'hover:border-border-hover-light dark:hover:border-border-hover-dark',
  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
);

export const selectClasses = clsx(inputClasses, 'pr-10 cursor-pointer appearance-none');

interface FieldRowProps {
  label: string;
  /** Make this field span both columns of TaskMetadataGrid. */
  full?: boolean;
  children: ReactNode;
}

export function FieldRow({ label, full = false, children }: FieldRowProps) {
  return (
    <div className={clsx('space-y-2', full && '@[340px]:col-span-2')}>
      <h4 className="text-sm md:text-[13px] font-medium text-text-tertiary-light dark:text-text-tertiary-dark">
        {label}
      </h4>
      {children}
    </div>
  );
}

interface FieldSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  className?: string;
}

export const FieldSelect = forwardRef<HTMLSelectElement, FieldSelectProps>(
  function FieldSelect({ className, children, ...rest }, ref) {
    return (
      <div className="relative">
        <select ref={ref} className={clsx(selectClasses, className)} {...rest}>
          {children}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary-light dark:text-text-tertiary-dark pointer-events-none" />
      </div>
    );
  },
);

interface TaskMetadataGridProps {
  children: ReactNode;
}

export function TaskMetadataGrid({ children }: TaskMetadataGridProps) {
  return (
    <div className="@container">
      <div className="grid grid-cols-1 @[340px]:grid-cols-2 gap-x-3 gap-y-2.5">
        {children}
      </div>
    </div>
  );
}
